import * as React from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Save, Eye, ExternalLink, Upload, Globe, Store, ImageIcon, Copy, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { fmtCents, fmtDuration } from "@/lib/time";
import { toast } from "sonner";

/**
 * Plus — marketplace profile editor.
 * TEAL-ACCENTED (product boundary tell). Rest of shell stays violet.
 */
const DAYS = [
  ["mon","Mon"], ["tue","Tue"], ["wed","Wed"], ["thu","Thu"],
  ["fri","Fri"], ["sat","Sat"], ["sun","Sun"],
];
const VERTICALS = ["hospitality","retail","salon","fitness","other"];

export default function Plus() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("plus.manage");
  const [profile, setProfile] = React.useState(null);
  const [f, setF] = React.useState(null);
  const [services, setServices] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/companies/${companyId}/plus/profile`),
        api.get(`/companies/${companyId}/plus/services`),
      ]);
      setProfile(p.data);
      setF({
        listing_name: p.data.listing_name,
        description: p.data.description || "",
        vertical: p.data.vertical || "other",
        hours: p.data.hours,
        marketplace_visible: !!p.data.marketplace_visible,
      });
      setServices(s.data.services);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  async function save() {
    if (!f || busy) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/companies/${companyId}/plus/profile`, f);
      setProfile(data);
      toast.success("Plus profile saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function toggleVisible(v) {
    setF(x => ({ ...x, marketplace_visible: v }));
    try {
      const { data } = await api.put(`/companies/${companyId}/plus/profile`,
                                      { marketplace_visible: v });
      setProfile(data);
      toast.success(v ? "Live" : "Hidden");
    } catch (e) {
      setF(x => ({ ...x, marketplace_visible: !v }));
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  async function uploadCover(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/companies/${companyId}/plus/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data);
      toast.success("Cover uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  const publicUrl = profile ? `${BACKEND}/api/public/plus/${profile.slug}` : "";
  const marketplacePreviewUrl = profile ? `${BACKEND}/api/public/plus/${profile.slug}` : "";

  async function copyLink() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy link"); }
  }

  if (!profile || !f) return <div className="p-6 text-[12px] text-brand-muted" data-testid="plus-loading">Loading…</div>;

  return (
    <div className="plus-scope px-4 md:px-6 py-4 md:py-6 max-w-[1100px]" data-testid="plus-page">
      {/* Plus scoped header (teal) */}
      <div className="plus-header flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-brand-teal text-white flex items-center justify-center shadow-soft">
            <Sparkles size={18}/>
          </div>
          <div className="min-w-0">
            <div className="text-[19px] font-bold tracking-tight text-brand-ink">Plus marketplace</div>
            <div className="text-[11.5px] text-brand-muted">Your listing on the Fable Plus consumer app. Teal is the marketplace boundary.</div>
          </div>
        </div>
        <div className="flex-1"/>
        <div className="flex items-center gap-2 flex-wrap">
          {profile.marketplace_visible ? (
            <span className="chip chip-teal mono-num text-[10px]" data-testid="plus-visibility-chip">
              <Globe size={10}/> live
            </span>
          ) : (
            <span className="chip mono-num text-[10px]" data-testid="plus-visibility-chip">
              hidden
            </span>
          )}
          <a href={marketplacePreviewUrl} target="_blank" rel="noreferrer" className="btn-outline"
             data-testid="plus-preview">
            <ExternalLink size={13}/> Preview JSON
          </a>
          {canManage && (
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-brand-teal/30 bg-card px-2 py-1.5" data-testid="plus-visibility-toggle">
              <input type="checkbox" checked={f.marketplace_visible}
                     onChange={(e)=>toggleVisible(e.target.checked)}/>
              <span className="text-[12px] font-medium">Visible in marketplace</span>
            </label>
          )}
        </div>
      </div>

      {/* Public URL band */}
      <div className="mt-3 rounded-md border border-brand-teal/25 bg-card/60 px-3 py-2 flex items-center gap-2"
           data-testid="plus-url-band">
        <Store size={13} className="accent-teal"/>
        <div className="text-[11.5px] text-brand-muted">Public URL</div>
        <span className="mono-num text-[11.5px] text-brand-ink truncate flex-1">
          /api/public/plus/{profile.slug}
        </span>
        <button className="btn-ghost !h-7 !px-2" onClick={copyLink} data-testid="plus-copy-url">
          {copied ? <><CheckCircle2 size={12} className="text-emerald-600"/> Copied</> : <><Copy size={12}/> Copy</>}
        </button>
      </div>

      {/* Grid: cover + form + services */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cover / basics */}
        <div className="lg:col-span-2 space-y-4">
          <div className="plus-card">
            <div className="plus-card-head">
              <div>
                <div className="micro-label">Cover image</div>
                <div className="mt-0.5 text-[13px] font-semibold text-brand-ink">The hero on your marketplace tile</div>
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                         onChange={(e) => uploadCover(e.target.files?.[0])}
                         data-testid="plus-cover-file"/>
                  <button className="btn-teal" onClick={() => fileRef.current?.click()}
                          data-testid="plus-cover-upload">
                    <Upload size={13}/> Upload
                  </button>
                </div>
              )}
            </div>
            <div className="plus-card-body">
              {profile.cover_url ? (
                <img src={`${BACKEND}${profile.cover_url}`} alt="cover"
                     className="w-full h-52 object-cover rounded-md border border-brand-teal/20"
                     data-testid="plus-cover-preview"/>
              ) : (
                <div className="w-full h-52 rounded-md border border-dashed border-brand-teal/40 bg-brand-teal/5 flex items-center justify-center text-brand-teal">
                  <div className="flex items-center gap-2 text-[12px]">
                    <ImageIcon size={16}/> Upload a cover to make this listing pop.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="plus-card">
            <div className="plus-card-head">
              <div>
                <div className="micro-label">Listing</div>
                <div className="mt-0.5 text-[13px] font-semibold text-brand-ink">Name, description, vertical</div>
              </div>
              {canManage && (
                <button className="btn-teal" onClick={save} disabled={busy} data-testid="plus-save">
                  <Save size={13}/> {busy ? "…" : "Save"}
                </button>
              )}
            </div>
            <div className="plus-card-body space-y-3">
              <div>
                <label className="micro-label">Listing name</label>
                <input className="field-input mt-1" value={f.listing_name}
                       onChange={(e)=>setF(x=>({...x, listing_name:e.target.value}))}
                       disabled={!canManage}
                       data-testid="plus-name"/>
                <div className="text-[10.5px] text-brand-faint mt-1">Slug regenerates from name — expect a fresh <span className="mono-num">/{slugify(f.listing_name || "")}</span></div>
              </div>
              <div>
                <label className="micro-label">Vertical</label>
                <select className="field-input mt-1" value={f.vertical}
                        onChange={(e)=>setF(x=>({...x, vertical:e.target.value}))}
                        disabled={!canManage}
                        data-testid="plus-vertical">
                  {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="micro-label">Description</label>
                <textarea className="field-input mt-1 !h-24" value={f.description}
                          onChange={(e)=>setF(x=>({...x, description:e.target.value}))}
                          disabled={!canManage}
                          data-testid="plus-desc"/>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div className="plus-card">
            <div className="plus-card-head">
              <div>
                <div className="micro-label">Opening hours</div>
                <div className="mt-0.5 text-[13px] font-semibold text-brand-ink">Per weekday</div>
              </div>
              {canManage && (
                <button className="btn-teal" onClick={save} disabled={busy} data-testid="plus-save-hours">
                  <Save size={13}/> {busy ? "…" : "Save"}
                </button>
              )}
            </div>
            <div className="plus-card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {DAYS.map(([key, label]) => {
                  const row = f.hours?.[key] || { open: "09:00", close: "17:00", closed: false };
                  return (
                    <div key={key} className="rounded-md border border-brand-teal/20 px-3 py-2 flex items-center gap-2">
                      <div className="w-8 mono-num text-[11px] uppercase tracking-[0.14em] text-brand-teal font-semibold">
                        {label}
                      </div>
                      {row.closed ? (
                        <span className="chip mono-num text-[10.5px]">closed</span>
                      ) : (
                        <>
                          <input type="time" className="field-input !h-8 !w-24 mono-num" value={row.open}
                                 onChange={(e)=>setF(x=>({...x, hours: {...x.hours, [key]: {...row, open:e.target.value}}}))}
                                 disabled={!canManage}
                                 data-testid={`plus-hours-${key}-open`}/>
                          <span className="text-brand-faint">→</span>
                          <input type="time" className="field-input !h-8 !w-24 mono-num" value={row.close}
                                 onChange={(e)=>setF(x=>({...x, hours: {...x.hours, [key]: {...row, close:e.target.value}}}))}
                                 disabled={!canManage}
                                 data-testid={`plus-hours-${key}-close`}/>
                        </>
                      )}
                      <div className="flex-1"/>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={row.closed}
                               onChange={(e)=>setF(x=>({...x, hours: {...x.hours, [key]: {...row, closed:e.target.checked}}}))}
                               disabled={!canManage}
                               data-testid={`plus-hours-${key}-closed`}/>
                        <span className="text-[10.5px] text-brand-muted">closed</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Listed services rail */}
        <div className="plus-card h-fit lg:sticky lg:top-4" data-testid="plus-services">
          <div className="plus-card-head">
            <div>
              <div className="micro-label">On your listing</div>
              <div className="mt-0.5 text-[13px] font-semibold text-brand-ink">Listed services</div>
            </div>
            <span className="chip chip-teal mono-num text-[10.5px]">{services.length}</span>
          </div>
          <div className="plus-card-body space-y-2">
            {services.length === 0 && (
              <div className="text-[12px] text-brand-muted">
                No services listed on Plus yet. Toggle <b>Listed on Plus</b> on active
                services in <b>Bookings → Services</b>.
              </div>
            )}
            {services.map((s) => (
              <div key={s.id} className="rounded-md border border-brand-teal/25 bg-card px-3 py-2"
                   data-testid={`plus-service-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-brand-ink truncate">{s.name}</div>
                    <div className="text-[10.5px] text-brand-muted truncate">{s.description || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono-num text-[12px] font-semibold accent-teal">{fmtCents(s.price_cents)}</div>
                    <div className="mono-num text-[10px] text-brand-muted">{fmtDuration(s.duration_mins)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "listing";
}
